import { Application } from "@core/Application";
import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { PresenteeismFailureAttemptsService } from "@services/PresenteeismFailureAttemptsService";
import { DateTriggerButton, RefreshButton } from "@shared/designSystem/Button";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer
} from "@shared/designSystem/Containers";
import { Modal } from "@shared/designSystem/messages/Modal";
import SearchBar from "@shared/designSystem/SearchBar";
import UserCard from "@shared/designSystem/UserCard";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { useEffect, useMemo, useRef, useState } from "react";

import {
    PresenteeismFailureListItem,
    usePresenteeismFailuresScreenViewModel,
} from "./PresenteeismFailuresScreenViewModel";
import PresenteeismFailureAttemptDetailsModal from "./PresenteeismFailureAttemptDetailsModal";
import LocalDateSelector from "./LocalDateSelector";

interface PresenteeismFailuresScreenProps {
    orgID: string;
}

const PresenteeismFailuresScreen: React.FC<PresenteeismFailuresScreenProps> = ({ orgID }) => {
    const service = useMemo(() => {
        return new PresenteeismFailureAttemptsService(Application.shared().coreDependencies.httpClient);
    }, []);
    const [selectedAttempt, setSelectedAttempt] = useState<PresenteeismFailureListItem | null>(null);
    const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
    const hasTrackedShownRef = useRef(false);

    const viewModel = usePresenteeismFailuresScreenViewModel({
        orgID,
        service,
    });

    useEffect(() => {
        if (hasTrackedShownRef.current) {
            return;
        }

        hasTrackedShownRef.current = true;
        AnalyticsHelper.trackPresenteeismFailuresShown(viewModel.day);
    }, [viewModel.day]);

    const controlsHeight = designSystemStyles.sizeRowMedium;
    const headerHeight = designSystemStyles.sizeRowMedium;
    const columnWidths = {
        user: "30%",
        reason: "30%",
        building: "20%",
        time: "20%",
    };

    const loadingView = (
        <QontyLoading
            text={"Cargando intentos fallidos...".localized()}
            withQonty={true}
        />
    );

    const emptyView = (
        <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
            <QontyEmptyState
                text={"No hay intentos fallidos para mostrar.".localized()}
                darkMode={false}
            />
        </VerticalContainer>
    );

    const loadedView = (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: designSystemStyles.gapMd, width: "100%" }}>
            <TableHeaderContainer height={headerHeight}>
                <TableHeaderCell text={"Usuario".localized()} width={columnWidths.user} />
                <TableHeaderCell text={"Motivo".localized()} width={columnWidths.reason} />
                <TableHeaderCell text={"Edificio".localized()} width={columnWidths.building} />
                <TableHeaderCell text={"Fecha".localized()} width={columnWidths.time} justifyContent="right" />
            </TableHeaderContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflowY: "auto", flex: 1, minHeight: 0, width: "100%"  }}>
                {viewModel.rows.map((row: PresenteeismFailureListItem) => (
                    <ClickableWrapper
                        key={row.id}
                        onClick={() => {
                            AnalyticsHelper.trackPresenteeismFailureDetailShown(
                                row.attempt.firestoreRecordId,
                                row.attempt.ticketID,
                                row.attempt.type,
                                row.attempt.failureReason,
                                row.attempt.day
                            );
                            setSelectedAttempt(row);
                        }}
                        style={{ width: "100%" }}
                    >
                        <TableRowContainer>
                            <HorizontalContainer width={columnWidths.user}>
                                <UserCard
                                    name={row.userName}
                                    width="100%"
                                />
                            </HorizontalContainer>
                            <TableCell text={row.failureReasonLabel} width={columnWidths.reason} truncate={true} />
                            <TableCell text={row.buildingName || "N/D".localized()} width={columnWidths.building} truncate={true} />
                            <TableCell
                                text={viewModel.formatTimestamp(row.timestamp)}
                                width={columnWidths.time}
                                justifyContent="right"
                                truncate={true}
                            />
                        </TableRowContainer>
                    </ClickableWrapper>
                ))}
            </div>
        </div>
    );

    return (
        <VerticalContainer height="100%" gap={designSystemStyles.gapLg} margin={designSystemStyles.paddingZero}>
            <HorizontalContainer height={controlsHeight}>
                <SearchBar
                    placeholder={"Buscar por usuario, motivo o edificio".localized()}
                    value={viewModel.searchField}
                    onChange={viewModel.setSearchField}
                />
                <DateTriggerButton
                    text={viewModel.formattedDay}
                    onClick={() => setIsDateSelectorOpen(true)}
                    width="190px"
                />
                <RefreshButton text="" onClick={() => void viewModel.refresh()} />
            </HorizontalContainer>

            {/* <HorizontalContainer justifyContent="space-between" width="100%">
                <SmallText text={`Registros: ${viewModel.rows.length}`} color={designSystemStyles.colorTextSecondary} />
            </HorizontalContainer> */}

            {viewModel.loading
                ? loadingView
                : viewModel.rows.length === 0
                    ? emptyView
                    : loadedView}
            <PresenteeismFailureAttemptDetailsModal
                attempt={selectedAttempt?.attempt ?? null}
                isOpen={selectedAttempt != null}
                onClose={() => setSelectedAttempt(null)}
            />
            <Modal
                isOpen={isDateSelectorOpen}
                onClose={() => setIsDateSelectorOpen(false)}
                title={"Seleccionar fecha".localized()}
                width="420px"
                maxWidth="92vw"
            >
                <LocalDateSelector
                    initialDate={viewModel.selectedDate}
                    events={{
                        onApply: (date) => {
                            const nextDate = date ?? new Date();
                            viewModel.setSelectedDate(nextDate);
                            AnalyticsHelper.trackPresenteeismFailuresDateChanged(
                                viewModel.getDayFromDate(nextDate)
                            );
                            setIsDateSelectorOpen(false);
                        },
                        onClear: () => {
                            const nextDate = new Date();
                            viewModel.setSelectedDate(nextDate);
                            AnalyticsHelper.trackPresenteeismFailuresDateChanged(
                                viewModel.getDayFromDate(nextDate)
                            );
                            setIsDateSelectorOpen(false);
                        },
                    }}
                />
            </Modal>
        </VerticalContainer>
    );
};

export default PresenteeismFailuresScreen;
